import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { RefreshCcw, Home } from "lucide-react";

interface FallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

const ErrorBoundaryFallback = ({
  error,
  resetErrorBoundary,
}: FallbackProps) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-red-600">
            Oops! Something went wrong
          </CardTitle>
          <CardDescription>
            We apologize for the inconvenience. An unexpected error has
            occurred.
          </CardDescription>
        </CardHeader>

        <CardContent>
          <Alert variant="destructive" className="mb-4">
            <AlertTitle className="font-medium">Error Details</AlertTitle>
            <AlertDescription className="mt-2 font-mono text-sm break-all">
              {error.message || "An unknown error occurred"}
            </AlertDescription>
          </Alert>

          {process.env.NODE_ENV === "development" && (
            <div className="mt-4 p-4 bg-gray-100 rounded-md">
              <h4 className="text-sm font-medium mb-2">Stack Trace:</h4>
              <pre className="text-xs overflow-auto whitespace-pre-wrap">
                {error.stack}
              </pre>
            </div>
          )}
        </CardContent>

        <CardFooter className="flex gap-4 justify-end">
          <Button
            variant="outline"
            onClick={() => window.location.replace("/")}
          >
            <Home className="mr-2 h-4 w-4" />
            Go Home
          </Button>

          <Button onClick={resetErrorBoundary}>
            <RefreshCcw className="mr-2 h-4 w-4" />
            Try Again
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default ErrorBoundaryFallback;
